import { reportError } from "@medimo/monitor";
import type { ResourcesConfig } from "aws-amplify";
import { fetchAuthSession } from "aws-amplify/auth";

const userPoolConfig = {
  userPoolId: process.env.NEXT_PUBLIC_USER_POOL_ID,
  userPoolClientId: process.env.NEXT_PUBLIC_COGNITO_CLIENT_ID,
  userPoolEndpoint: `${process.env.NEXT_PUBLIC_COGNITO_ENDPOINT}/idp`,
};

export const amplifyConfig: ResourcesConfig = {
  Auth: process.env.NEXT_PUBLIC_IDENTITY_POOL_ID
    ? {
        Cognito: {
          ...userPoolConfig,
          identityPoolId: process.env.NEXT_PUBLIC_IDENTITY_POOL_ID,
          identityPoolEndpoint: `${process.env.NEXT_PUBLIC_COGNITO_ENDPOINT}/identity`,
        },
      }
    : { Cognito: userPoolConfig },
  Storage: {
    S3: {
      bucket: process.env.NEXT_PUBLIC_MAIN_STORAGE_BUCKET,
      region: "ap-northeast-1",
    },
  },
};

// User Pool のみの設定。Next.js middleware でのトークン有無判定、および
// Identity Pool に登録しないアプリ（medimo-chat 等）本体の Amplify.configure に使う。
// identityPoolId を含めると fetchAuthSession がリクエスト毎に
// GetCredentialsForIdentity を呼び出し、Cognito のレート制限
// (TooManyRequestsException) に達して認証済みユーザーが未認証扱いになるため、
// Identity Pool を除外して User Pool のトークン検証・リフレッシュのみ行う。
export const amplifyConfigWithoutIdentityPool: ResourcesConfig = {
  Auth: {
    Cognito: {
      userPoolId: process.env.NEXT_PUBLIC_USER_POOL_ID,
      userPoolClientId: process.env.NEXT_PUBLIC_COGNITO_CLIENT_ID,
      userPoolEndpoint: `${process.env.NEXT_PUBLIC_COGNITO_ENDPOINT}/idp`,
    },
  },
};

const MAX_RETRY_ATTEMPTS = 3;
const RETRY_DELAY_BASE = 300;

export const getJWT = async (): Promise<string> => {
  let lastError: Error | undefined;

  for (let attempt = 0; attempt <= MAX_RETRY_ATTEMPTS; attempt++) {
    try {
      const forceRefresh = attempt > 0;
      const { tokens } = await fetchAuthSession({ forceRefresh });
      const jwt = tokens?.idToken?.toString();

      if (jwt) {
        return jwt;
      }

      if (attempt === MAX_RETRY_ATTEMPTS) {
        break;
      }

      const delay = RETRY_DELAY_BASE * 2 ** attempt;
      await new Promise((resolve) => setTimeout(resolve, delay));
    } catch (error) {
      lastError = error as Error;

      if (attempt === MAX_RETRY_ATTEMPTS) {
        break;
      }

      const delay = RETRY_DELAY_BASE * 2 ** attempt;
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  // console.error は @error.source:console 扱いで SLO 集計対象外になるため、
  // 実害の認証エラーとして RUM に明示報告する（source:custom → フロント可用性 SLO で捕捉）。
  // メッセージは Error Tracking でのグルーピングを安定させるため固定し、原因は context に載せる。
  reportError(new Error("Failed to get JWT after retries"), {
    errorType: "jwt_refresh_failed",
    cause: lastError?.message,
  });
  throw new Error("認証情報の取得に失敗しました。再度ログインをお試しください");
};
